import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { api } from '@/lib/api'
import { ArrowLeft, Loader2, Mail, CheckCircle } from 'lucide-react'

export function ForgotPasswordPage() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)

  const forgotPasswordMutation = useMutation({
    mutationFn: (email: string) => api.forgotPassword(email),
    onSuccess: () => {
      setSubmitted(true)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (email) {
      forgotPasswordMutation.mutate(email)
    }
  }

  const handleContinue = () => {
    navigate(`/reset-password?email=${encodeURIComponent(email)}`)
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="mt-6 text-2xl font-bold text-gray-900">Check your email</h2>
            <p className="mt-2 text-gray-600">
              We've sent a verification code to <strong>{email}</strong>
            </p>
          </div>
          <div className="mt-6 space-y-3">
            <Button onClick={handleContinue} className="w-full">
              Enter verification code
            </Button>
            <Link to="/login">
              <Button variant="outline" className="w-full">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to login
              </Button>
            </Link>
          </div>
          <p className="text-center text-sm text-gray-500">
            Didn't receive the email?{' '}
            <button
              onClick={() => forgotPasswordMutation.mutate(email)}
              className="text-primary hover:underline"
              disabled={forgotPasswordMutation.isPending}
            >
              Click to resend
            </button>
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="flex items-center justify-center space-x-2 mb-2">
            <div className="w-10 h-10 bg-gradient-to-r from-spectrum-teal to-spectrum-blue rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">L</span>
            </div>
          </div>
          <h1 className="text-center text-3xl font-bold text-gray-900">LinkToCreative</h1>
          <h2 className="mt-6 text-center text-2xl font-semibold text-gray-900">
            Forgot your password?
          </h2>
          <p className="mt-2 text-center text-gray-600">
            No worries, we'll send you reset instructions.
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Email address
            </label>
            <div className="mt-1 relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                id="email"
                type="email"
                required
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {forgotPasswordMutation.isError && (
            <div className="text-red-500 text-sm text-center">
              Failed to send reset email. Please try again.
            </div>
          )}

          <Button
            type="submit"
            className="w-full"
            disabled={forgotPasswordMutation.isPending || !email}
          >
            {forgotPasswordMutation.isPending && (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            )}
            Reset password
          </Button>

          <div className="text-center">
            <Link
              to="/login"
              className="text-sm text-gray-600 hover:text-gray-900 inline-flex items-center"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to login
            </Link>
          </div>
        </form>
      </div>
    </div>
  )
}
